/**
 * Constants for reader selection tool validation and UI behavior.
 *
 * These limits control validation for various reader features (dictionary,
 * translate, TTS, AI) and ensure consistent UX across the application.
 */

/**
 * Duration in milliseconds for tooltip auto-hide across all validation components.
 */
export const TOOLTIP_AUTO_HIDE_DURATION = 3000;

/**
 * Dictionary feature validation limits.
 */
export const DICTIONARY_MAX_WORDS = 5;

/**
 * Translation feature validation limits.
 *
 * Character limit rationale (1000 chars):
 * - User experience: Covers typical paragraph-level translations
 * - API efficiency: ~250 input tokens, ~500 output tokens (600 budget with margin)
 * - Encrypted payload: Max 1900 characters (accounts for AES + base64 overhead)
 * - Comparison: 5x smaller than AI features, 10x smaller than TTS
 */
export const TRANSLATE_MAX_CHARACTERS = 1000;

/**
 * Text-to-Speech (TTS) feature validation limits.
 *
 * Frontend limit rationale (10,000 chars):
 * - Audio duration: ~10-11 minutes (realistic listening session)
 * - API efficiency: ~20 sequential requests for Latin text (chunked at ~500
 *   chars); multibyte scripts chunk smaller under the byte budget, up to ~56
 *   requests for CJK and ~74 for emoji-heavy text
 * - User experience: Lower failure rate, faster processing
 * - Comparison: 2.5x larger than AI features, 12.5x larger than translate
 */
export const TTS_MAX_CHARACTERS = 10000;

/**
 * Maximum characters per TTS request, as accepted by the backend.
 * Text is split into chunks of at most this size by splitTextIntoSpeechChunks
 * before being sent, one request per chunk.
 */
export const TTS_CHUNK_SIZE = 500;

/**
 * Maximum UTF-8 bytes per TTS chunk.
 *
 * The backend caps the encrypted payload at 750 characters. AES-GCM adds a
 * 16-byte tag and base64 grows in whole 4-character blocks:
 * encryptedLength = 4 * ceil((utf8Bytes + 16) / 3). 545 bytes encrypt to 748
 * characters; 546 already produce 752. The derivation assumes Encrypter
 * prepends no salt to the payload; a salt shrinks the real budget.
 */
export const TTS_CHUNK_MAX_UTF8_BYTES = 545;

/**
 * Playback speed options for TTS.
 * Range: 0.5x (slow) to 2x (fast), based on common accessibility standards.
 * - 0.5x-0.75x: For users who need slower speech
 * - 1x: Normal speed (default)
 * - 1.25x-2x: For users who prefer faster playback
 */
export const TTS_PLAYBACK_SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

/**
 * Default playback speed for TTS (normal speed).
 */
export const TTS_DEFAULT_PLAYBACK_SPEED = 1;

/**
 * AI Integration feature validation limits.
 */
export const AI_MIN_CHARACTERS = 10;
export const AI_MAX_CHARACTERS = 4000;
